<template>
  <div class="personal-page-wrapper">
    <h1>Личные данные</h1>
    <ul class="profile_submenu">
      <li>
        <router-link to="/personal">
          {{ "Личные данные" }}
        </router-link>
      </li>
      <li>
        <router-link to="/password">
          {{ "Изменить пароль" }}
        </router-link>
      </li>
    </ul>
    <validation-observer
        v-slot="{ handleSubmit }"
        ref="refFormObserver"
    >
      <b-form
          @submit.stop.prevent="handleSubmit(submitToUser)"
          class="form validate"
      >
        <!-- Avatar Upload -->
        <b-form-group
            label="Аватар"
            label-for="avatar"
        >
          <div class="d-flex align-items-center">
            <div class="mr-3">
              <b-avatar
                  :src="avatarPreview || avatarUrl"
                  size="100px"
                  variant="light-primary"
              >
                <template v-if="!avatarPreview && !avatarUrl">
                  <feather-icon icon="UserIcon" size="50" />
                </template>
              </b-avatar>
            </div>
            <div>
              <b-form-file
                  id="avatar"
                  v-model="avatarFile"
                  accept="image/*"
                  placeholder="Выберите файл аватара..."
                  browse-text="Обзор"
                  @change="onAvatarChange"
              />
              <small class="text-muted">Рекомендуется: JPG, PNG или GIF, размер до 2MB</small>
            </div>
          </div>
        </b-form-group>

        <!-- Photo Upload -->
        <b-form-group
            label="Фотография"
            label-for="photo"
        >
          <div class="d-flex align-items-start">
            <div class="mr-3" v-if="photoPreview || photoUrl">
              <b-img
                  :src="photoPreview || photoUrl"
                  fluid
                  thumbnail
                  style="max-width: 200px; max-height: 200px;"
              />
            </div>
            <div class="flex-grow-1">
              <b-form-file
                  id="photo"
                  v-model="photoFile"
                  accept="image/*"
                  placeholder="Выберите файл фотографии..."
                  browse-text="Обзор"
                  @change="onPhotoChange"
              />
              <small class="text-muted">Рекомендуется: JPG, PNG или GIF, размер до 2MB</small>
            </div>
          </div>
        </b-form-group>

        <!-- Name -->
        <validation-provider
            v-slot="validationContext"
            name="name"
            rules="required"
        >
          <b-form-group
              label="Имя"
              label-for="name"
          >
            <b-form-input
                placeholder="Введите ваше имя"
                id="name"
                :state="getValidationState(validationContext)"
                v-model="formData.name"
                trim
            />
            <b-form-invalid-feedback>
              {{ validationContext.errors[0] }}
            </b-form-invalid-feedback>
          </b-form-group>
        </validation-provider>

        <!-- Email (readonly) -->
        <b-form-group
            label="Email"
            label-for="email"
        >
          <b-form-input
              id="email"
              readonly
              :value="email"
              trim
          />
        </b-form-group>

        <!-- Level -->
        <b-form-group
            label="Уровень знаний"
            label-for="level"
        >
          <b-form-select
              id="level"
              v-model="formData.level"
              :options="levelOptions"
          />
        </b-form-group>

        <!-- Preferences (chip input) -->
        <b-form-group
            label="Предпочтения"
            label-for="preferences"
            description="Введите предпочтение и нажмите Enter или добавьте"
        >
          <b-form-tags
              id="preferences"
              v-model="formData.preferences"
              add-on-change
              no-outer-focus
              placeholder="Добавьте предпочтение..."
              tag-variant="primary"
              tag-pills
              remove-on-delete
          >
            <template v-slot="{ tags, inputAttrs, inputHandlers, addTag, removeTag }">
              <b-input-group class="mb-2">
                <b-form-input
                    v-bind="inputAttrs"
                    v-on="inputHandlers"
                    placeholder="Добавить предпочтение..."
                    class="form-control"
                />
                <b-input-group-append>
                  <b-button @click="addTag()" variant="primary">Добавить</b-button>
                </b-input-group-append>
              </b-input-group>
              <div class="d-flex flex-wrap">
                <b-form-tag
                    v-for="tag in tags"
                    :key="tag"
                    :title="tag"
                    variant="primary"
                    pill
                    @remove="removeTag(tag)"
                    class="mr-1 mb-1"
                >
                  {{ tag }}
                </b-form-tag>
              </div>
            </template>
          </b-form-tags>
        </b-form-group>

        <!-- Purpose/Aims (chip input) -->
        <b-form-group
            label="Цели обучения"
            label-for="purpose"
            description="Введите цель и нажмите Enter или добавьте"
        >
          <b-form-tags
              id="purpose"
              v-model="formData.purpose"
              add-on-change
              no-outer-focus
              placeholder="Добавьте цель..."
              tag-variant="success"
              tag-pills
              remove-on-delete
          >
            <template v-slot="{ tags, inputAttrs, inputHandlers, addTag, removeTag }">
              <b-input-group class="mb-2">
                <b-form-input
                    v-bind="inputAttrs"
                    v-on="inputHandlers"
                    placeholder="Добавить цель..."
                    class="form-control"
                />
                <b-input-group-append>
                  <b-button @click="addTag()" variant="success">Добавить</b-button>
                </b-input-group-append>
              </b-input-group>
              <div class="d-flex flex-wrap">
                <b-form-tag
                    v-for="tag in tags"
                    :key="tag"
                    :title="tag"
                    variant="success"
                    pill
                    @remove="removeTag(tag)"
                    class="mr-1 mb-1"
                >
                  {{ tag }}
                </b-form-tag>
              </div>
            </template>
          </b-form-tags>
        </b-form-group>

        <!-- Interests (chip input) -->
        <b-form-group
            label="Интересы"
            label-for="interests"
            description="Введите интерес и нажмите Enter или добавьте"
        >
          <b-form-tags
              id="interests"
              v-model="formData.interests"
              add-on-change
              no-outer-focus
              placeholder="Добавьте интерес..."
              tag-variant="info"
              tag-pills
              remove-on-delete
          >
            <template v-slot="{ tags, inputAttrs, inputHandlers, addTag, removeTag }">
              <b-input-group class="mb-2">
                <b-form-input
                    v-bind="inputAttrs"
                    v-on="inputHandlers"
                    placeholder="Добавить интерес..."
                    class="form-control"
                />
                <b-input-group-append>
                  <b-button @click="addTag()" variant="info">Добавить</b-button>
                </b-input-group-append>
              </b-input-group>
              <div class="d-flex flex-wrap">
                <b-form-tag
                    v-for="tag in tags"
                    :key="tag"
                    :title="tag"
                    variant="info"
                    pill
                    @remove="removeTag(tag)"
                    class="mr-1 mb-1"
                >
                  {{ tag }}
                </b-form-tag>
              </div>
            </template>
          </b-form-tags>
        </b-form-group>

        <b-button type="submit" variant="primary" :disabled="isSubmitting">
          <b-spinner small v-if="isSubmitting" class="mr-1" />
          {{ isSubmitting ? 'Сохранение...' : 'Сохранить' }}
        </b-button>
      </b-form>
    </validation-observer>
  </div>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import {
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BFormFile,
  BAvatar,
  BImg,
  BFormSelect,
  BFormTags,
  BFormTag,
  BInputGroup,
  BInputGroupAppend,
  BSpinner
} from 'bootstrap-vue'
import {ValidationObserver, ValidationProvider} from 'vee-validate'

const {
  mapState,
  mapActions,
} = createNamespacedHelpers('user');

export default {
  name: 'PersonalIndex',
  components: {
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    ValidationProvider,
    ValidationObserver,
    BButton,
    BFormFile,
    BAvatar,
    BImg,
    BFormSelect,
    BFormTags,
    BFormTag,
    BInputGroup,
    BInputGroupAppend,
    BSpinner
  },
  computed: {
    ...mapState({
      email: (state) => state.userData.email,
      userData: (state) => state.userData,
    }),
    avatarUrl() {
      if (this.userData.avatar) {
        const baseUrl = process.env.NODE_ENV !== 'production'
          ? 'https://tredbackend.test'
          : 'https://mytredspace.ru';
        return `${baseUrl}/storage/${this.userData.avatar}`;
      }
      return null;
    },
    photoUrl() {
      if (this.userData.photo) {
        const baseUrl = process.env.NODE_ENV !== 'production'
          ? 'https://tredbackend.test'
          : 'https://mytredspace.ru';
        return `${baseUrl}/storage/${this.userData.photo}`;
      }
      return null;
    }
  },
  data() {
    return {
      formData: {
        name: '',
        level: 'beginner',
        preferences: [],
        purpose: [],
        interests: []
      },
      avatarFile: null,
      photoFile: null,
      avatarPreview: null,
      photoPreview: null,
      isSubmitting: false,
      levelOptions: [
        { value: 'beginner', text: 'Начинающий' },
        { value: 'intermediate', text: 'Средний' },
        { value: 'advanced', text: 'Продвинутый' }
      ]
    };
  },
  methods: {
    ...mapActions(['changeUser', 'getUser']),
    getValidationState({dirty, validated, valid = null}) {
      return dirty || validated ? valid : null;
    },
    onAvatarChange(event) {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.avatarPreview = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },
    onPhotoChange(event) {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.photoPreview = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },
    submitToUser() {
      this.isSubmitting = true;

      // Create FormData to handle file uploads
      const formData = new FormData();

      formData.append('name', this.formData.name);
      formData.append('level', this.formData.level);

      // Append arrays as JSON strings
      if (this.formData.preferences && this.formData.preferences.length > 0) {
        this.formData.preferences.forEach((pref, index) => {
          formData.append(`preferences[${index}]`, pref);
        });
      }

      if (this.formData.purpose && this.formData.purpose.length > 0) {
        this.formData.purpose.forEach((purp, index) => {
          formData.append(`purpose[${index}]`, purp);
        });
      }

      if (this.formData.interests && this.formData.interests.length > 0) {
        this.formData.interests.forEach((interest, index) => {
          formData.append(`interests[${index}]`, interest);
        });
      }

      // Append files if they exist
      if (this.avatarFile) {
        formData.append('avatar', this.avatarFile);
      }

      if (this.photoFile) {
        formData.append('photo', this.photoFile);
      }

      this.changeUser(formData)
          .then(() => {
            // Refresh user data to get updated avatar/photo URLs
            return this.getUser();
          })
          .then(() => {
            this.$bvToast.toast('Ваши данные успешно изменены', {
              title: `Успешно! Данные обновлены`,
              variant: 'success',
              solid: true,
            });
            // Clear file inputs
            this.avatarFile = null;
            this.photoFile = null;
            this.avatarPreview = null;
            this.photoPreview = null;
          })
          .catch((err) => {
            console.error('Error updating profile:', err);
            this.$bvToast.toast('Пожалуйста, попробуйте позже', {
              title: `Ошибка! Что-то пошло не так...`,
              variant: 'danger',
              solid: true,
            })
          })
          .finally(() => {
            this.isSubmitting = false;
          });
    },
    initializeFormData() {
      this.formData.name = this.userData.name || '';
      this.formData.level = this.userData.level || 'beginner';
      this.formData.preferences = Array.isArray(this.userData.preferences)
        ? [...this.userData.preferences]
        : [];
      this.formData.purpose = Array.isArray(this.userData.purpose)
        ? [...this.userData.purpose]
        : [];
      this.formData.interests = Array.isArray(this.userData.interests)
        ? [...this.userData.interests]
        : [];
    }
  },
  mounted() {
    this.initializeFormData();
  },
  watch: {
    userData: {
      handler() {
        this.initializeFormData();
      },
      deep: true
    }
  }
};
</script>

<style lang="scss" scoped>
.personal-page-wrapper {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.profile_submenu {
  display: flex;
  list-style: none;
  padding: 0;
  margin-bottom: 30px;
  border-bottom: 2px solid #e9ecef;

  li {
    margin-right: 20px;

    a {
      display: block;
      padding: 10px 15px;
      text-decoration: none;
      color: #6c757d;
      border-bottom: 2px solid transparent;
      margin-bottom: -2px;

      &:hover,
      &.router-link-active {
        color: #7367f0;
        border-bottom-color: #7367f0;
      }
    }
  }
}
</style>
